import '../financials/financialsResponsive.css';
import ResponsiveRow from '../shared/ResponsiveRow';
import { useState, useEffect, useMemo, useCallback } from 'react';
import ChartTableView from './ChartTableView';
import AddChartAccountModal from '../modals/AddChartAccountModal';
import HoverHelp from '../shared/HoverHelp';
import useModuleAccess from '../../hooks/useModuleAccess';

function ChartOfAccountsView({ chartOfAccounts, onAdd, onUpdate, onDelete, userId, isUser = false }) {
    const { allowed: financialEditAllowed } = useModuleAccess(isUser ? 'financial' : null, 'edit');
    const canEdit = !isUser || financialEditAllowed;
    const [editingId, setEditingId] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [chartViewMode, setChartViewMode] = useState('dashboard'); // 'dashboard' or 'table'
     const incomeAccounts = chartOfAccounts.filter(a => a.type === 'income');
    const expenseAccounts = chartOfAccounts.filter(a => a.type === 'expense');
     return (
        <div className="ia-financials ia-chart-of-accounts">
            <ResponsiveRow className="ia-financial-header flex justify-between items-center mb-6">
                <div className="flex items-center gap-4">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800">Chart of Accounts</h2>
                        <p className="text-sm text-gray-600 mt-1">Manage your account categories</p>
                    </div>
                </div>
                <ResponsiveRow className="flex items-center gap-4">
                    {/* View Toggle */}
                    <div className="flex items-center gap-3">
                        <span className={`text-sm ${chartViewMode === 'dashboard' ? 'font-bold text-blue-600' : 'text-gray-600'}`}>
                            Dashboard View
                        </span>
                        <button
                            aria-label="Toggle chart view"
                            onClick={() => setChartViewMode(chartViewMode === 'dashboard' ? 'table' : 'dashboard')}
                            type="button"
                            role="switch"
                            aria-checked={chartViewMode === 'table'}
                            className="ia-chart-view-toggle"
                        >
                            <span className="ia-chart-view-track" aria-hidden="true"><span className="ia-chart-view-knob" /></span>
                        </button>
                        <span className={`text-sm ${chartViewMode === 'table' ? 'font-bold text-blue-600' : 'text-gray-600'}`}>
                            Table View
                        </span>
                        <HoverHelp helpId="financials.chart-view-mode" userId={userId} />
                    </div>
                    {canEdit && <span className="inline-flex items-center">
                        <button onClick={() => setShowAddModal(true)} className="bg-[var(--btn-fill)] text-[var(--btn-ink)] px-6 py-3 rounded-lg font-medium hover:bg-[var(--btn-fill-hover)] transition">
                            + Add Account Category
                        </button>
                        <HoverHelp helpId="financials.add-account-category" userId={userId} />
                    </span>}
                </ResponsiveRow>
            </ResponsiveRow>
             {/* Conditional Rendering based on view mode */}
            {chartViewMode === 'dashboard' ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Income Accounts */}
                <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <span className="text-green-600">📈</span> Income Accounts
                    </h3>
                    <div className="space-y-2">
                        {incomeAccounts.map(account => (
                            <div key={account.id} className="flex justify-between items-center p-3 border rounded-lg hover:bg-gray-50">
                                <div>
                                    <div className="font-medium">{account.name}</div>
                                    {account.description && <div className="text-sm text-gray-600">{account.description}</div>}
                                </div>
                                {canEdit && <div className="flex gap-2">
                                    <button onClick={() => onUpdate(account)} className="text-blue-600 hover:text-blue-800" title="Edit">✏️</button>
                                    <button onClick={() => onDelete(account.id)} className="text-red-600 hover:text-red-800" title="Delete">🗑️</button>
                                </div>}
                            </div>
                        ))}
                        {incomeAccounts.length === 0 && (
                            <div className="text-center text-gray-500 py-4">No income accounts yet</div>
                        )}
                    </div>
                </div>
                 {/* Expense Accounts */}
                <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <span className="text-red-600">📉</span> Expense Accounts
                    </h3>
                    <div className="space-y-2">
                        {expenseAccounts.map(account => (
                            <div key={account.id} className="flex justify-between items-center p-3 border rounded-lg hover:bg-gray-50">
                                <div>
                                    <div className="font-medium">{account.name}</div>
                                    {account.description && <div className="text-sm text-gray-600">{account.description}</div>}
                                </div>
                                {canEdit && <div className="flex gap-2">
                                    <button onClick={() => onUpdate(account)} className="text-blue-600 hover:text-blue-800" title="Edit">✏️</button>
                                    <button onClick={() => onDelete(account.id)} className="text-red-600 hover:text-red-800" title="Delete">🗑️</button>
                                </div>}
                            </div>
                        ))}
                        {expenseAccounts.length === 0 && (
                            <div className="text-center text-gray-500 py-4">No expense accounts yet</div>
                        )}
                    </div>
                </div>
            </div>
            ) : (
                /* Table View */
                <ChartTableView 
                    chartOfAccounts={chartOfAccounts}
                    onUpdate={onUpdate}
                    onDelete={onDelete}
                    canEdit={canEdit}
                />
            )}
             {canEdit && showAddModal && (
                <AddChartAccountModal
                    onClose={() => setShowAddModal(false)}
                    onSave={(data) => {
                        onAdd(data);
                        setShowAddModal(false);
                    }}
                />
            )}
        </div>
    );
}


export default ChartOfAccountsView;
